import type { AttributeOption } from "@imminently/decisively-ui/AttributePicker";
import AttributeRichTextEditor, { type AttributeRichTextFormat } from "@imminently/decisively-ui/AttributeRichTextEditor";
import Field from "@imminently/decisively-ui/Field";
import { useField } from "@imminently/decisively-ui/Form";
import type * as React from "react";

export interface AttributeTemplateFieldProps {
  name: string;
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  attributes: AttributeOption[];
  groupOrder?: string[];
  /** `"html"` stores formatted HTML, `"text"` stores plain text. Both use `{{ }}` tokens. */
  format?: AttributeRichTextFormat;
  singleLine?: boolean;
  /** No toolbar: `@` to insert, and a hover-only Raw toggle. */
  compact?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Form-bound `AttributeRichTextEditor`: stores the template string (`{{ }}` tokens,
 * as HTML or plain text per `format`) at `name`.
 *
 * The editor reads the form value once, on mount. To pick up a value that changes
 * from outside the editor (a form reset, or a `format` change), remount it with a `key`.
 */
export function AttributeTemplateField({
  name,
  label,
  helperText,
  required,
  disabled = false,
  attributes,
  groupOrder,
  format = "html",
  singleLine = false,
  compact = false,
  placeholder,
  className,
}: AttributeTemplateFieldProps) {
  const field = useField<string>({ name, required });

  return (
    <Field
      className={className}
      name={name}
      fullWidth
    >
      {label ? <Field.Label required={required ?? field.required}>{label}</Field.Label> : null}
      <Field.Control>
        <AttributeRichTextEditor
          attributes={attributes}
          groupOrder={groupOrder}
          format={format}
          singleLine={singleLine}
          compact={compact}
          defaultValue={field.value ?? ""}
          onChange={(value) => field.onValueChange(value.template)}
          disabled={disabled}
          placeholder={placeholder}
        />
      </Field.Control>
      <Field.Message message={helperText} />
    </Field>
  );
}
